import { Button } from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import type { CompactActionInfo } from "./compact-denker-actions";
import { installStagedDesktopUpdate } from "./use-auto-update";
import { desktopVersionLabel } from "@/lib/desktop-version-label";
import { useT } from "@/i18n";
import { updatesMessages } from "@/i18n/messages/updates";

interface CompactUpdateActionButtonProps {
  isInstalling: boolean;
  isOpen: boolean;
  onInfoChange: (info: CompactActionInfo | null) => void;
  readyVersion: string;
}

export function CompactUpdateActionButton({
  isInstalling,
  isOpen,
  onInfoChange,
  readyVersion,
}: CompactUpdateActionButtonProps) {
  const t = useT(updatesMessages);
  const version = desktopVersionLabel(readyVersion);
  const info = {
    label: t("updateTo", { version }),
    description: [t("restartToInstall")],
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-accent transition-colors hover:bg-white/20 disabled:opacity-60"
      data-testid="desktop-update-ready-button"
      aria-label={t("installAndRestart", { version })}
      disabled={isInstalling}
      tabIndex={isOpen ? 0 : -1}
      onBlur={() => onInfoChange(null)}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onInfoChange(null);
        void installStagedDesktopUpdate();
      }}
      onFocus={() => onInfoChange(info)}
      onPointerEnter={() => onInfoChange(info)}
      onPointerLeave={() => onInfoChange(null)}
    >
      {isInstalling ? (
        <Icons.Spinner className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Icons.Download className="h-3.5 w-3.5" />
      )}
    </Button>
  );
}
